import { EnergiekostenAusgangsdaten } from './projektion/EnergiekostenAusgangsdaten';
import { EnergiekostenKostenentwicklung } from './projektion/EnergiekostenKostenentwicklung';
import { EnergiekostenPreisentwicklung } from './projektion/EnergiekostenPreisentwicklung';
import { EnergiekostenPrognose } from './projektion/EnergiekostenPrognose';
import { EnergiekostenPrognoseOptionen } from './projektion/EnergiekostenPrognoseOptionen';
import { EnergiekostenQuellen } from './projektion/EnergiekostenQuellen';
import { getBasisjahr, getParameter } from './projektion/Energiekosten';
import { getEnergietraegerKeyMap } from '../../main/functions/getEnergietraegerKeyMap';
import { IDs } from '../../helpers/IDs';
import { Md5 } from 'ts-md5/dist/md5';
import * as React from 'react';
import { renderText } from '../dateneingabe/assistent/assistent_helpers';
import style from './ZusatzToolsCO2.less';
import styles from './projektion/Energiekosten.less';

export interface ZusatzToolsProjektionProps {
    data: any;
}

export interface ZusatzToolsProjektionState {
    energietraeger: any;
    hasData: boolean;
    inflation: any;
    md5: string;
    option: string;
    values: any;
}

export class ZusatzToolsProjektion extends React.Component<ZusatzToolsProjektionProps, ZusatzToolsProjektionState> {

    constructor(props: any) {
        super(props);
        this.state = {
            energietraeger: {},
            hasData: false,
            inflation: { checked: true, value: 2.1 },
            md5: null,
            option: 'uba_studie',
            values: null,
        };
        this.mapEnergietraeger = getEnergietraegerKeyMap() || {};
    }

    private mapEnergietraeger: any;
    private optionen: any = { uba_studie: 'Lt. UBA-Studie', custom: 'Eigene Annahmen' };

    componentDidMount(): void {
        const energietraeger: any = {};
        const parameter: any = getParameter(this.props.data) || {};
        const hasSavedData: boolean = !!Object.keys(parameter).length;
        const inflation: any = parameter.inflation || this.state.inflation;
        const option: string = (parameter.option || {}).id || this.state.option || null;

        Object.keys(this.mapEnergietraeger).forEach((key: string) => {
            const jahr: number = hasSavedData
                ? ((parameter.energietraeger || {})[key] || {}).jahr
                : getBasisjahr(this.mapEnergietraeger[key], this.props.data);
            const checked: number = hasSavedData
                ? ((parameter.energietraeger || {})[key] || {}).checked
                : !!jahr;
            energietraeger[key] = { checked, jahr };
        });
        const hasData: boolean = !!Object.keys(energietraeger).reduce((jahre: number, key: string) =>
            jahre + +!!energietraeger[key].jahr, 0
        );
        this.setState({ energietraeger, hasData, inflation, option });
    }

    componentDidUpdate(): void {
        if (this.getMd5() !== this.state.md5) {
            this.setState({ md5: this.getMd5() });
        }
    }

    getMd5(): string {
        return Md5.hashAsciiStr(JSON.stringify((this.props.data || {})[IDs.KENNZIFFERN_BERECHNET] || {})).toString();
    }

    render(): JSX.Element {
        if (!this.state.hasData) {
            return this.renderIntro(true);
        }
        const { md5 } = this.state;

        return (
            <div key={`ZusatzToolsProjektion_${md5}`} className={styles.energiekosten_projektion} style={{ width: 1200, marginLeft: 10 }}>
                <div style={{ marginTop: -10 }}>
                    {renderText('Energiekosten-Projektion - Einleitungstext', this.props.data)}
                </div>
                <div style={{ marginBottom: 24, display: 'flex', flex: 1, flexDirection: 'row' }}>
                    <EnergiekostenAusgangsdaten
                        data={this.props.data}
                        onChange={(energietraeger: any) => this.setState({ energietraeger})}
                        selection={this.state.energietraeger}
                    />
                    <div style={{ minWidth: 20, width: 20 }}>&nbsp;</div>
                    <EnergiekostenPrognoseOptionen
                        data={this.props.data}
                        inflation={this.state.inflation}
                        onOptionChange={(option: string) => this.setState({ option })}
                        onInflationChange={(inflation: any) => this.setState({ inflation })}
                        optionen={this.optionen}
                        selection={this.state.option}
                    />
                </div>
                <EnergiekostenPrognose
                    data={this.props.data}
                    energietraeger={this.state.energietraeger}
                    getData={(values: any) => this.setState({ values })}
                    inflation={this.state.inflation}
                    onOptionChange={(option: string) => this.setState({ option })}
                    option={this.state.option}
                    optionen={this.optionen}
                />
                <div style={{ marginTop: 24, marginBottom: 24, display: 'flex', flex: 1, flexDirection: 'row' }}>
                    <div
                        style={{
                            margin: '0 5px 0 1px', display: 'flex', flex: 1, flexDirection: 'column',
                            flexBasis: '50%', boxSizing: 'border-box', backgroundColor: '#f7f7f7',
                            boxShadow: '0px 0px 10px var(--color-wform-shadow)'
                        }}
                    >
                        <EnergiekostenPreisentwicklung
                            data={this.props.data}
                            values={this.state.values}
                        />
                    </div>
                    <div
                        style={{
                            margin: '0 1px 0 10px', display: 'flex', flex: 1, flexDirection: 'column',
                            flexBasis: '50%', boxSizing: 'border-box', backgroundColor: '#f7f7f7',
                            boxShadow: '0px 0px 10px var(--color-wform-shadow)'
                        }}
                    >
                        <EnergiekostenKostenentwicklung
                            data={this.props.data}
                            values={this.state.values}
                        />
                    </div>
                </div>
                <div>
                    <EnergiekostenQuellen
                        data={this.props.data}
                    />
                </div>
            </div>
        );
    }

    renderIntro(nodata: boolean = false): JSX.Element {
        return (
            <div className={style.intro}>
                <div className={style.introText} style={{ maxWidth: 'calc(100% - 50px)' }}>
                    {nodata
                        ? <div>Bitte geben Sie zur Nutzung dieses Tools zunächst Energiedaten für mindestens ein Jahr ein.</div>
                        : <div>Die jeweiligen Jahresverbräuche zieht das Tool automatisch aus den im Rahmen der Dateneingabe hinterlegten Energieverbrauchsdaten.</div>
                    }
                </div>
            </div>
        );
    }
}
